import { calculateProxyTypeSubtotals } from '@fd/common/biz/pricing';
import { ProxyTypeMetas, ProxyTypeName } from '@fd/common/biz/proxyType';
import { X } from 'lucide-react';
import Card from '@/components/ui/Card';
import ProxyIcon from '@/assets/icons/ProxyIcon';
import usePaymentDetails from '@/store/paymentDetailsStore';

interface OrderSummaryItemProps {
  type: ProxyTypeName;
  title: string;
  quantity: number;
  pricePerUnit: number;
  onRemove: () => void;
}

export default function OrderSummaryLineItem({
  type,
  title,
  quantity,
  pricePerUnit,
  onRemove,
}: OrderSummaryItemProps) {
  const { coupon } = usePaymentDetails();
  const totals = calculateProxyTypeSubtotals({
    trafficGb: quantity,
    proxyType: ProxyTypeMetas[type],
    discountPercentage: coupon?.type === 'percentage' ? coupon.discountValue : undefined,
    discountFixed: coupon?.type === 'fixed_amount' ? coupon.discountValue : undefined,
  });

  return (
    <Card>
      <div className="flex items-start justify-between mb-4">
        <div className="flex gap-3">
          <div className="bg-proxy-icon-bg p-1.5 rounded-lg hidden sm:flex items-center justify-center h-8 w-8">
            <ProxyIcon type={type} className="stroke-white h-5 w-5" />
          </div>
          <div>
            <div className="font-medium text-proxy-text-dark text-sm">
              {title}
            </div>
            <div className="text-sm sm:text-xs text-secondary">
              Shared - Payment per GB
            </div>
          </div>
        </div>
        <button onClick={onRemove}>
          <X className="h-4 w-4 text-secondary" />
        </button>
      </div>
      <div className="border-t border-proxy-border py-3"></div>
      <div className="flex flex-col gap-2 text-sm">
        <div className="flex justify-between">
          <span className="text-secondary">Item</span>
          <span className="font-medium">{quantity}GB</span>
        </div>
        <div className="flex justify-between">
          <span className="text-secondary">Cost</span>
          <span>${pricePerUnit.toFixed(2)}/GB</span>
        </div>
        {totals.discountSource !== 'none' && (
          <div className="flex justify-between">
            <span className="text-secondary">
              {totals.discountSource === 'discountPercentage' ? 'Coupon discount' : 'Tier discount'} {totals.discountPct}%
            </span>
            <span>
              -${(totals.subtotalPrice - totals.totalPrice).toFixed(2)}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-secondary">Subtotal</span>
          <div className="flex gap-2">
            {totals.tierDiscountPct > 0 && (
              <div className="text-muted line-through">
                {totals.subtotalPriceText}
              </div>
            )}
            <span>{totals.totalPriceText}/month</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
